
import { useState } from 'react';
import { reportsApi } from '../../lib/api';
import { useAuth } from '../../hooks/useAuth';
import { Modal } from './Modal';
import { Button } from './Button';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  contentType: 'post' | 'comment' | 'group_post' | 'message' | 'user';
  contentId: string;
  reportedUserId: string;
  onReportSubmitted?: () => void;
}

const REPORT_REASONS = [
  { value: 'spam', label: 'Spam or unwanted content' },
  { value: 'harassment', label: 'Harassment or bullying' },
  { value: 'hate_speech', label: 'Hate speech or discrimination' },
  { value: 'inappropriate_content', label: 'Inappropriate or offensive content' },
  { value: 'misinformation', label: 'False or misleading information' },
  { value: 'violence', label: 'Violence or threats' },
  { value: 'copyright', label: 'Copyright infringement' },
  { value: 'other', label: 'Other (please specify)' }
];

export default function ReportModal({ 
  isOpen, 
  onClose, 
  contentType, 
  contentId, 
  reportedUserId,
  onReportSubmitted 
}: ReportModalProps) {
  const { user } = useAuth();
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !reason) return;

    setLoading(true);
    setError('');

    try {
      // Submit report via API
      const response = await reportsApi.createReport({
        contentType,
        contentId,
        reportedUserId,
        reason,
        description: description.trim() || undefined
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to submit report');
      }

      setSuccess(true);
      onReportSubmitted?.();
      
      // Auto close after success
      setTimeout(() => {
        onClose();
        setSuccess(false);
        setReason('');
        setDescription('');
      }, 2000);

    } catch (error: any) {
      setError(error.message || 'Failed to submit report');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setReason('');
      setDescription('');
      setError('');
      setSuccess(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Report Content">
      {success ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-check-line text-2xl text-green-600"></i>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Report Submitted</h3>
          <p className="text-gray-600">
            Thank you for helping keep our community safe. We'll review your report shortly.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Why are you reporting this content? *
            </label>
            <div className="space-y-2">
              {REPORT_REASONS.map((reasonOption) => (
                <label key={reasonOption.value} className="flex items-center">
                  <input
                    type="radio"
                    name="reason"
                    value={reasonOption.value}
                    checked={reason === reasonOption.value}
                    onChange={(e) => setReason(e.target.value)}
                    className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300"
                    required
                  />
                  <span className="ml-3 text-sm text-gray-700">{reasonOption.label}</span>
                </label>
              ))}
            </div>
          </div>

          {(reason === 'other' || reason) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional details {reason === 'other' ? '*' : '(optional)'}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Please provide more details about why you're reporting this content..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                rows={4}
                maxLength={500}
                required={reason === 'other'}
              />
              <p className="text-sm text-gray-500 mt-1">
                {description.length}/500 characters
              </p>
            </div>
          )}

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <i className="ri-information-line text-yellow-600 mt-0.5 mr-3"></i>
              <div className="text-sm text-yellow-800">
                <p className="font-medium mb-1">Before reporting:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Make sure the content violates our community guidelines</li>
                  <li>Consider blocking the user if you don't want to see their content</li>
                  <li>False reports may result in restrictions on your account</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <Button
              type="button"
              onClick={handleClose}
              variant="outline"
              disabled={loading}
              className="whitespace-nowrap"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              disabled={!reason}
              className="bg-red-600 hover:bg-red-700 text-white whitespace-nowrap"
            >
              Submit Report
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
